import { useState, useEffect, useCallback } from "react";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import typography from "../../../assets/theme/base/typography";
import FaiButton from "../../../MuiComponents/FaiButton";
import FaiSelect from "../../../MuiComponents/FaiSelect";
import pxToRem from "../../../assets/theme/functions/pxToRem";
import logo from "../../../assets/images/logos/approved.png";
import {
	Box,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Grid,
	Chip,
	CircularProgress,
	Snackbar,
	Alert,
	Button,
	Pagination,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorIcon from "@mui/icons-material/ErrorRounded";
import NoteIcon from "@mui/icons-material/NoteAltRounded";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FaiPagination from "../../../MuiComponents/FaiPagination";
import theme from "../../../assets/theme";
import { ProductListItem, ProductsResponse } from "../../../types/productTypes";
import NoDrafts from "./NoDrafts";
import { ProductController } from "../../../controllers/productController";
import EditDraftModal from "./EditDraftModal";
import { toast } from "react-toastify";

const Drafts = () => {
	const { palette, borders } = theme;
	const [products, setProducts] = useState<ProductListItem[]>([]);
	const [sortOption, setSortOption] = useState("last");
	const [deleteModal, setDelete] = useState(false);
	const [productToDelete, setProductToDelete] =
		useState<ProductListItem | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pagination, setPagination] = useState({
		currentPage: 0,
		totalPages: 0,
		totalElements: 0,
	});
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
		"success"
	);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [draftLoading, setDraftLoading] = useState<any>(false);
	const [selectedProduct, setSelectedProduct] =
		useState<ProductListItem | null>(null);

	const fetchDraftProducts = useCallback(
		async (page = 0, size = 10, retryCount = 0) => {
			setLoading(true);
			setError(null);
			try {
				const response = await ProductController.getMerchantProducts({
					page,
					size,
					productId: "",
					categoryId: "",
					brand: "",
					productName: "",
					approvalStatus: "",
					isDraft: true,
				});

				if (response?.data?.status === 200 && response?.data?.data) {
					const productListData = response.data.data;
					setProducts(productListData.data);
					setPagination({
						currentPage: page,
						totalPages: productListData.totalPages,
						totalElements: productListData.totalElements,
					});
				} else {
					setSnackbarMessage(
						"Received an invalid response format from the server."
					);
					setSnackbarSeverity("error");
					setSnackbarOpen(true);
					setProducts([]);
				}
			} catch (err: any) {
				if (err.message === "Network Error" && retryCount < 3) {
					setTimeout(() => {
						fetchDraftProducts(page, size, retryCount + 1);
					}, 2000);
					return;
				}

				console.error("Error fetching draft products:", err);
				setError("Failed to load draft products. Please try again.");
				setSnackbarMessage(
					`Failed to load products: ${
						err.message || "Unknown error"
					}. Please try again later.`
				);
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
				setProducts([]);
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	useEffect(() => {
		fetchDraftProducts();
	}, [fetchDraftProducts]);

	const handleSortChange = (selectedOption) => {
		setSortOption(selectedOption.value);
	};

	const confirmDelete = async () => {
		if (!productToDelete) return;

		setDeleteLoading(true);
		try {
			const response = await ProductController.deleteProduct(
				productToDelete.id
			);
			if (response?.status === 204) {
				setSnackbarMessage("Product deleted successfully");
				setSnackbarSeverity("success");
				setSnackbarOpen(true);

				// Refresh the list after delete
				fetchDraftProducts(pagination.currentPage);
			} else {
				setSnackbarMessage("Failed to delete product. Please try again later.");
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
			}
		} catch (err: any) {
			console.error("Error deleting product:", err);
			setSnackbarMessage(
				`Failed to delete product: ${
					err.message || "Unknown error"
				}. Please try again later.`
			);
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setDeleteLoading(false);
			setDelete(false);
			setProductToDelete(null);
		}
	};

	const handleCloseModal = () => {
		setDelete(false);
		setProductToDelete(null);
	};

	const handleDeleteClick = (product: ProductListItem) => {
		setProductToDelete(product);
		setDelete(true);
	};

	const handleEditClick = (product: ProductListItem) => {
		setSelectedProduct(product);
		setEditModalOpen(true);
	};

	const handleCloseEditModal = () => {
		setEditModalOpen(false);
		setSelectedProduct(null);
	};

	const handleProductUpdated = () => {
		fetchDraftProducts(pagination.currentPage);
		setSnackbarMessage("Product updated successfully");
		setSnackbarSeverity("success");
		setSnackbarOpen(true);
	};

	const handleEditError = (errorMessage: string) => {
		setSnackbarMessage(errorMessage);
		setSnackbarSeverity("error");
		setSnackbarOpen(true);
	};

	const moveProductToInventory = async (pId, pData = {}) => {
		try {
			if (draftLoading) return;
			setDraftLoading(pId);
			const { data } = await ProductController.updateProduct(pId, {
				isDraft: false,
				// ...pData,
			});
			toast.success(data.message);
			fetchDraftProducts(pagination.currentPage);
		} catch (error: any) {
			// Surface the backend's rejection message — e.g. the "account not
			// approved" gate — instead of failing silently.
			console.error(error);
			toast.error(
				error?.response?.data?.message ||
					"Could not move the product to inventory. Please try again."
			);
		} finally {
			setDraftLoading(false);
		}
	};

	const handlePageChange = (
		event: React.ChangeEvent<unknown>,
		page: number
	) => {
		fetchDraftProducts(page - 1); // API is 0-indexed, but pagination component is 1-indexed
	};

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	const sortOptions = [
		{ value: "last", label: "Last Updated on" },
		{ value: "id", label: "Product ID", disabled: true },
	];

	if (loading && products.length === 0) {
		return (
			<FaiBox
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="60vh"
			>
				<CircularProgress />
			</FaiBox>
		);
	}

	if (error && products.length === 0) {
		return (
			<FaiBox
				display="flex"
				justifyContent="center"
				alignItems="center"
				flexDirection="column"
				height="60vh"
			>
				<FaiTypography variant="h6" color="error" mb={2}>
					{error}
				</FaiTypography>
				<FaiButton
					variant="contained"
					color="primary"
					onClick={() => fetchDraftProducts()}
				>
					Try Again
				</FaiButton>
			</FaiBox>
		);
	}

	if (products.length === 0) {
		return <NoDrafts />;
	}

	return (
		<FaiBox>
			<FaiBox
				display="flex"
				justifyContent="space-between"
				alignItems="flex-end"
				mb={3}
			>
				<FaiBox sx={{ width: 240 }}>
					<FaiTypography
						variant="caption"
						fontWeight="bold"
						mb={1}
						display="block"
					>
						Sort By
					</FaiTypography>
					<FaiSelect
						placeholder="Sort by"
						value={sortOptions.find((option) => option.value === sortOption)}
						onChange={handleSortChange}
						options={sortOptions}
					/>
				</FaiBox>
			</FaiBox>
			{products.map((product: any) => (
				<FaiBox
					key={product.id}
					mb={2}
					p={2.5}
					borderRadius={borders.borderRadius.md}
					boxShadow="0 2px 12px 0 rgba(0,0,0,0.05)"
					bgcolor={palette.white.main}
				>
					<Grid container spacing={2}>
						<Grid item xs={12} md={7}>
							<FaiBox display="flex" alignItems="center">
								<FaiBox
									component="img"
									src={logo}
									alt="Product"
									width={64}
									height={64}
									mr={2}
									borderRadius="8px"
								/>
								<FaiBox>
									<FaiTypography
										variant="h6"
										fontWeight={typography.fontWeightBold}
									>
										{product.productName}
									</FaiTypography>
									<FaiTypography variant="body2" color="text.secondary">
										ID: {product.id}
									</FaiTypography>
								</FaiBox>
							</FaiBox>
						</Grid>
						<Grid item xs={12} md={3}>
							<FaiBox
								display="flex"
								flexDirection="column"
								height="100%"
								justifyContent="center"
							>
								<FaiTypography variant="caption" color="text.secondary">
									Category
								</FaiTypography>
								<FaiTypography variant="body2">
									{product.categoryName}
								</FaiTypography>
								<FaiTypography variant="caption" color="text.secondary" mt={1}>
									Brand
								</FaiTypography>
								<FaiTypography variant="body2">
									{product.brand || "N/A"}
								</FaiTypography>
							</FaiBox>
						</Grid>
						<Grid item xs={12} md={2}>
							<FaiBox
								display="flex"
								flexDirection="column"
								height="100%"
								justifyContent="center"
								alignItems="flex-end"
							>
								<FaiBox display="flex" gap={1} mb={1}>
									<Chip
										icon={<NoteIcon />}
										label="Draft"
										size="small"
										color="warning"
									/>
								</FaiBox>
								<FaiBox display="flex" gap={1} mt={1} mb={1}>
									<IconButton
										size="small"
										color="primary"
										onClick={() => handleEditClick(product)}
										sx={{
											bgcolor: "rgba(0, 171, 85, 0.08)",
											"&:hover": { bgcolor: "rgba(0, 171, 85, 0.16)" },
										}}
									>
										<EditIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										color="error"
										onClick={() => handleDeleteClick(product)}
										sx={{
											bgcolor: "rgba(255, 72, 66, 0.08)",
											"&:hover": { bgcolor: "rgba(255, 72, 66, 0.16)" },
										}}
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</FaiBox>
								<FaiBox display="flex" gap={1} mb={1}>
									<Chip
										className={`!rounded-lg cursor-pointer ${
											draftLoading === product.id ? "disabled" : ""
										}`}
										label={
											draftLoading === product.id
												? "Loading..."
												: "Move to Inventory"
										}
										size="medium"
										color="success"
										onClick={() => {
											moveProductToInventory(product.id, {
												categoryId: product.categoryId,
												productTypeId: product.productTypeId,
											});
										}}
									/>
								</FaiBox>
							</FaiBox>
						</Grid>
					</Grid>
				</FaiBox>
			))}
			{pagination.totalPages > 1 && (
				<FaiBox display="flex" justifyContent="center" mt={4} mb={2}>
					<Pagination
						count={pagination.totalPages}
						page={pagination.currentPage + 1}
						onChange={handlePageChange}
					/>
				</FaiBox>
			)}
			<Dialog
				open={deleteModal}
				onClose={handleCloseModal}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>
					<FaiBox
						display="flex"
						justifyContent="space-between"
						alignItems="center"
					>
						<FaiTypography
							variant="h5"
							fontWeight={typography.fontWeightMedium}
						>
							Delete Draft Listing
						</FaiTypography>
						<IconButton onClick={handleCloseModal} aria-label="close">
							<CloseIcon />
						</IconButton>
					</FaiBox>
				</DialogTitle>
				<DialogContent dividers>
					<FaiTypography mb={1}>
						{productToDelete ? (
							<>
								Are you sure you want to delete{" "}
								<strong>{productToDelete.productName}</strong>?
							</>
						) : (
							"Are you sure you want to delete this draft listing?"
						)}
					</FaiTypography>
					<FaiTypography variant="body2" color="text.secondary">
						This action cannot be undone.
					</FaiTypography>
				</DialogContent>
				<DialogActions>
					<FaiButton
						onClick={handleCloseModal}
						color="secondary"
						variant="outlined"
						disabled={deleteLoading}
					>
						Cancel
					</FaiButton>
					<FaiButton
						onClick={confirmDelete}
						color="error"
						variant="contained"
						startIcon={<DeleteIcon />}
						disabled={deleteLoading}
					>
						{deleteLoading ? "Deleting..." : "Delete"}
					</FaiButton>
				</DialogActions>
			</Dialog>
			<EditDraftModal
				open={editModalOpen}
				onClose={handleCloseEditModal}
				product={selectedProduct}
				onProductUpdated={handleProductUpdated}
				onError={handleEditError}
			/>
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbarSeverity}
					sx={{ width: "100%" }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</FaiBox>
	);
};

export default Drafts;
